import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import store from '@state/store'
import { intelligenceV2 } from '@modules/aawz-intelligence-v2/constants/URL'
import { formatQueryParams } from '@utils/Format'
import { ERRO_INESPERADO } from '@modules/aawz-intelligence-v2/api/guardas'

const ERR_ID_OBRIGATORIO = { message: 'id obrigatório', status: 400 }

const DEFAULT_STORE_MODULE = 'aawz-intelligence-v2'

/**
 * Teto de conversas carregadas por chamada — DONO ÚNICO do número.
 *
 * É o `N` que a sidebar exibe em "Ver todas (N)": o endpoint não devolve
 * contagem total (gap G8), então o que a UI mostra é o que coube neste limite.
 * Mora aqui, e não em quem chama, porque o valor é do contrato do endpoint;
 * escrito de novo em cada consumidor, viraria dois números diferentes na
 * primeira vez que alguém ajustasse um lado só.
 */
export const CONVERSAS_LIMITE_CARGA = 30

/**
 * Sentinela de LEITURA do eixo `context`: devolve as conversas de todas as
 * personas. Combinado com o backend em 25/08/2026 (spec-001 do
 * `project-new-ux`) e implementado lá em `d2272ee4c`.
 *
 * ⚠️ NÃO é uma persona e nunca é gravado: `CONVERSATION_CONTEXTS` (o domínio
 * da coluna) não o contém, então mandá-lo em qualquer endpoint de escrita
 * volta 400. Vive aqui, e não em `constants/Personas.js`, exatamente por
 * isso — o registro de personas espelha o do backend e tem drift-guard.
 *
 * ★ MUDOU O VALOR? É PR nos DOIS repos. O par vive em
 * `aawz-web-backend/src/aawz_intelligence_v2/constants/conversation_context.py`
 * (`CONTEXT_ALL_PERSONAS`), e cada lado tem seu teste:
 *   - aqui:    `api/contrato-listagem-todas-personas.unit.js` (lê o fonte de lá)
 *   - lá:      `test_conversas_todas_as_personas.py` (literal, de propósito)
 * Os dois travam o literal, mas nenhum aparece no momento em que alguém digita
 * o valor novo — este aviso é para isso. Divergir não quebra ruidosamente por
 * acidente: com o backend renomeado, o `choices` do parser devolve 400 e a
 * home para de listar; renomear só aqui manda um valor que o `choices` recusa.
 *
 * ★ [spec-010 T028] RENOMEADO para `CONTEXT_TODOS_OS_HUBS`
 * (`contracts/ui-components.md` §3) — o literal `'ALL'` é INTOCADO (contrato
 * de rede, ver acima). `CONTEXT_TODAS_AS_PERSONAS` continua exportado como
 * ALIAS do mesmo valor: `conversation-folder-actions.js` e `view-chat.vue`
 * ainda importam o nome antigo, e não são deste escopo — a remoção do alias é
 * varredura de quem terminar de migrar todos os call-sites (T033).
 */
export const CONTEXT_TODOS_OS_HUBS = 'ALL'
export const CONTEXT_TODAS_AS_PERSONAS = CONTEXT_TODOS_OS_HUBS

/**
 * A listagem devolve **404 com lista** quando não há conversa nenhuma — medido
 * na API real (`APIIaCrmConversas.get_`: `status=404`,
 * `message='Data not found'`, `value=[]`). Sem este discriminante, "não tem
 * conversa" fica indistinguível de "a rede caiu", e todo primeiro acesso é
 * recebido com "Verifique sua conexão".
 *
 * ⚠️ `Array.isArray`, e não `!r.value`: em falha REAL o `con.get` da casa
 * devolve `{ status, message, value: <Error> }` (`services/connection.js:176-184`
 * — ele nunca rejeita), então `value` presente não significa lista. Com a
 * versão frouxa, um 404 de REDE (URL errada, proxy) viraria "Nenhuma conversa
 * ainda" e a tela mentiria dizendo que o usuário nunca conversou.
 *
 * Mora aqui, e não na view, porque a listagem tem VÁRIOS consumidores — a tela
 * do chat, os dois recarregamentos que a própria coluna dispara (mover
 * conversa, apagar pasta) e o `apiFeeFixoConversas.listar` do guia. Duas
 * leituras do mesmo contrato de rede divergem em silêncio (SH-3), e foi assim
 * que o guia ficou para trás por um commit (S-1).
 *
 * ⚠️ Ao mexer neste predicado, varra os call-sites em vez de confiar nesta
 * frase — ela já esteve desatualizada: `grep -rn "apiConversations.carregar("`.
 *
 * @param {Object=} r resposta do `getConversations`
 * @returns {boolean} true quando a resposta é uma lista vazia legítima
 */
export const respostaEhListaVazia = (r) =>
  Boolean(r && r.status === 404 && Array.isArray(r.value))

/** A resposta traz lista utilizável (com itens ou vazia por contrato). */
export const respostaTemLista = (r) =>
  Boolean(r && (r.status === 200 || respostaEhListaVazia(r)))

function buildMissingIdResponse(template) {
  return template.buildErrorResponse(ERR_ID_OBRIGATORIO)
}

class APIConversations extends APITemplate {
  /**
   * Lista as conversas da persona. O backend particiona por `context`: omitir `persona`
   * mantém a listagem de hoje (IA Operacional).
   *
   * `id_cliente_app` filtra as conversas de UM cliente (spec-005 do
   * `project-onboarding-with-ai-fee-fixo`). É ADICIONAL ao gate de
   * escritório/profissional, nunca substituto. Ausente ⇒ query byte-idêntica à
   * de hoje: `formatQueryParams` descarta `null`/`undefined`, então quem nunca
   * passou o parâmetro não sente diferença. O rótulo é `id_cliente_app` porque é
   * assim que o backend o nomeia — termo divergente entre os dois lados falha
   * pelo lado silencioso (§4.3 do CLAUDE.md raiz).
   *
   * `context` é o MESMO eixo de `persona` e tem precedência sobre ela no
   * backend (`resource_conversations.py`) — por isso os dois nunca viajam
   * juntos aqui. Com `CONTEXT_TODAS_AS_PERSONAS` a listagem devolve todas as
   * personas: é o que a home por IA usa (spec-001 do `project-new-ux`), por
   * ser a porta da plataforma e mostrar tudo o que o consultor conversou com
   * as PERSONAS do motor de chat.
   *
   * ⚠️ "Todas as personas" não é "todo o eixo `context`": a Central de IA
   * (`AI_FRAMEWORK`) fica de FORA, porque não é persona — o backend recorta
   * com `IN` sobre as chaves do registry (`fb63cd48f`), depois de um MAJOR que
   * apontou que remover o predicado inteiro abria um passo além do que o owner
   * pediu. Medido no escritório 83: 23 conversas de agente que a coluna não
   * lista.
   *
   * Sai SÓ o eixo de persona. Escritório e recorte por profissional continuam
   * decididos no servidor, pela sessão, e nada aqui pode alargá-los — medido
   * no RDS dev: com `ALL` numa sessão não-gestor o WHERE saiu
   * `id_profissional = 895 AND is_deleted = false AND id_escritorio = 83`,
   * sem `context`. Ausente ⇒ query byte-idêntica à de hoje
   * (`formatQueryParams` descarta `undefined`).
   *
   * @param {Object} opts
   * @param {number=} opts.limit
   * @param {number=} opts.offset
   * @param {string=} opts.persona Id da persona (valor de `context`).
   * @param {number=} opts.id_cliente_app Só as conversas daquele cliente.
   * @param {string=} opts.context Eixo de persona; `ALL` = todas.
   */
  /**
   * `todosAgentes` é a forma que a lateral imersiva usa para pedir "tudo que eu
   * conversei, com qualquer agente". Ele NÃO viaja como `todos_agentes`: esse
   * nome não existe em lugar nenhum do backend (`grep -rn "todos_agentes"
   * aawz-web-backend/src` → vazio), e o `reqparse` descarta argumento não
   * registrado em silêncio — a lista voltaria filtrada pela persona default
   * ('CRM') sem 400 e sem log. O mecanismo que o backend implementa para o
   * MESMO pedido é `context=ALL` (`resource_conversations.py:121-124`,
   * `choices=READ_CONVERSATION_CONTEXTS`), então é ele que sai no fio.
   *
   * `idFolder` filtra as conversas de UMA pasta. ⚠️ MEDIDO em 27/08/2026: o
   * parser do GET registra `limit`, `offset`, `persona`, `context`,
   * `id_instance` e `id_cliente_app` — `id_folder` NÃO está lá
   * (`resource_conversations.py:105-131`), logo o backend o ignora. O
   * parâmetro continua saindo no fio (é o contrato pretendido, e entra em
   * vigor no dia em que o `add_argument` existir), mas quem depende do recorte
   * HOJE tem de filtrar pelo campo `id_folder` que vem em cada conversa
   * (`atributos` da listagem, `resource_conversations.py:184`) — é o que
   * `conversations.vue` faz.
   */
  /**
   * `hub` (spec-010 [T028], D6) — filtro por hub roteado
   * (`profissional`|`gestao`|`servicos`), mesmo EIXO de `context`/`persona`
   * (nunca viaja junto com eles — mesma exclusão mútua de sempre).
   *
   * ★ FORWARD-COMPATÍVEL, ainda não fim-a-fim: medido em 28/08/2026, o
   * `choices` do parser do backend (`READ_CONVERSATION_CONTEXTS`,
   * `constants/conversation_context.py`) ainda só aceita as 3 personas +
   * `AI_FRAMEWORK` + `ALL` — os 3 valores de hub entram nesse domínio só
   * depois de `constants/conversation_context.py` ser reescrito sobre `Hub`
   * ([T031], onda A release 2). Até lá, mandar `hub` aqui devolveria 400
   * ("context deve ser uma persona válida...") — por isso NENHUM call-site
   * deste escopo usa o parâmetro ainda; ele existe para T029 (agrupar a
   * lateral por hub) montar sobre algo, e o agrupamento de HOJE deve seguir
   * client-side, sobre `state.conversations` já carregado (mesmo padrão de
   * `conversationsByFolder`/`looseConversations` em `store/getters.js`).
   */
  async getConversations({
    limit = 20,
    offset = 0,
    persona,
    id_cliente_app,
    context,
    idFolder,
    todosAgentes,
    hub,
  } = {}) {
    const params = formatQueryParams({
      limit,
      offset,
      // `persona` e `context` são o MESMO eixo e nunca viajam juntos: o
      // backend dá precedência ao `context`, e mandar os dois deixa a intenção
      // ambígua na aba de rede.
      persona: todosAgentes || context || hub ? undefined : persona,
      id_cliente_app,
      context: todosAgentes ? CONTEXT_TODOS_OS_HUBS : context || hub,
      id_folder: idFolder,
    })
    const r = await con.get(intelligenceV2.conversations + params)
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /**
   * Loader compartilhado (fetch + dispatch): usado pelo Conversations após
   * mover conversa para pasta para refletir o novo agrupamento nas duas
   * superfícies (popup e página cheia) via a store compartilhada. A action
   * Vuex NUNCA faz fetch — a rede vive aqui.
   *
   * `storeModule` acompanha a prop de mesmo nome do Conversations: a lateral é
   * reusável por outro módulo, e sem o parâmetro a lista buscada com a persona
   * de um produto era gravada na store do outro. Default preserva o IA CRM.
   */
  async carregar({
    limit = CONVERSAS_LIMITE_CARGA,
    offset = 0,
    persona,
    id_cliente_app,
    context,
    todosAgentes,
    hub,
    storeModule = DEFAULT_STORE_MODULE,
  } = {}) {
    const r = await this.getConversations({
      limit,
      offset,
      persona,
      id_cliente_app,
      context,
      todosAgentes,
      hub,
    })
    // 404 com lista é vazio, não falha — mesmo contrato que a tela usa. Sem
    // isto, esvaziar a última pasta deixava a coluna mostrando a lista velha,
    // porque o `setConversations` nunca chegava a rodar.
    if (respostaTemLista(r)) {
      store.dispatch(storeModule + '/setConversations', r.value || [])
    }
    return r
  }

  async deleteConversation({ id } = {}) {
    if (!id) return buildMissingIdResponse(this)
    const r = await con.patch(intelligenceV2.conversation(id), {
      is_deleted: true,
    })
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  async getConversation({ id } = {}) {
    if (!id) return buildMissingIdResponse(this)
    const r = await con.get(intelligenceV2.conversation(id))
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  async renameConversation({ id, topic } = {}) {
    if (!id) return buildMissingIdResponse(this)
    if (!topic) {
      return this.buildErrorResponse({
        message: 'topic obrigatório',
        status: 400,
      })
    }
    const r = await con.patch(intelligenceV2.conversation(id), { topic })
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /**
   * spec-009 [T007] — vincula (ou troca, ou desvincula com `id_cliente_app:
   * null`) o cliente da conversa pelo MESMO PATCH que `renameConversation`
   * usa (`contracts/api.yaml`). É a única porta de escrita: o servidor
   * confere posse da conversa E do cliente antes de gravar
   * (`_find_owned_conversation` + `APIPermissoes.check_professional_client_app`)
   * — o front não decide permissão, só chama e lê o corpo (HTTP 200 sempre,
   * `status` real dentro dele).
   */
  async vincularCliente({ id, id_cliente_app } = {}) {
    if (!id) return buildMissingIdResponse(this)
    const r = await con.patch(intelligenceV2.conversation(id), {
      id_cliente_app: id_cliente_app === undefined ? null : id_cliente_app,
    })
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }
}

const apiConversations = new APIConversations()

export { apiConversations }
